import { useEffect, useMemo, useState } from "react";
import { MagnifyingGlass, UsersThree, User, WarningCircle, CheckCircle } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

const toggle = (list, id) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

export function RecipientPicker({ value, onChange, contacts, groups }) {
  const [q, setQ] = useState("");
  const [pv, setPv] = useState(null);
  const s = q.trim().toLowerCase();
  const gs = useMemo(() => groups.filter((g) => g.active !== false && g.name.toLowerCase().includes(s)), [groups, s]);
  const cs = useMemo(() => contacts.filter((c) => c.active !== false && c.name.toLowerCase().includes(s)), [contacts, s]);
  const key = JSON.stringify([value.contact_ids, value.group_ids, value.channels]);

  useEffect(() => {
    if (!value.contact_ids.length && !value.group_ids.length) { setPv(null); return undefined; }
    const t = setTimeout(() => {
      api.post("/service/recurring/recipients/preview", { contact_ids: value.contact_ids, group_ids: value.group_ids, channels: value.channels })
        .then(({ data }) => setPv(data)).catch(() => setPv(null));
    }, 250);
    return () => clearTimeout(t);
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  const Row = ({ id, kind, label, sub }) => {
    const field = kind === "group" ? "group_ids" : "contact_ids";
    return (
      <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm" data-testid={`recipient-option-${kind}-${id}`}>
        <Checkbox checked={value[field].includes(id)} onCheckedChange={() => onChange({ ...value, [field]: toggle(value[field], id) })} />
        {kind === "group" ? <UsersThree size={14} className="text-orange-500" /> : <User size={14} className="text-slate-400" />}
        <span className="font-medium text-slate-800 truncate">{label}</span>
        <span className="text-[11px] text-slate-400 truncate">{sub}</span>
      </label>
    );
  };

  return (
    <div className="space-y-2" data-testid="recipient-picker">
      <div className="relative">
        <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search contacts and groups" className="pl-8 h-9" data-testid="recipient-search-input" />
      </div>
      <div className="max-h-40 overflow-y-auto rounded-md border border-slate-200 p-1">
        {!gs.length && !cs.length && <div className="text-xs text-slate-400 p-2">No matches — add people with “Manage Recurring Contact List”.</div>}
        {gs.map((g) => <Row key={g.id} id={g.id} kind="group" label={g.name} sub={`${g.member_ids.length} member${g.member_ids.length === 1 ? "" : "s"}`} />)}
        {cs.map((c) => <Row key={c.id} id={c.id} kind="contact" label={c.name} sub={[c.phone, c.email].filter(Boolean).join(" · ")} />)}
      </div>
      {pv && (
        <div className="rounded-md bg-slate-50 border border-slate-200 p-2 space-y-1" data-testid="recipient-preview">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Who will be notified ({pv.recipients.filter((r) => r.eligible).length})</div>
          {pv.recipients.map((r) => (
            <div key={`${r.contact_id}-${r.channel}`} className="flex items-center gap-1.5 text-xs" data-testid={`recipient-preview-${r.contact_id}-${r.channel}`}>
              {r.eligible ? <CheckCircle size={13} className="text-emerald-600" /> : <WarningCircle size={13} className="text-amber-600" />}
              <span className="font-semibold text-slate-700">{r.name}</span>
              <span className="text-slate-400 uppercase">{r.channel}</span>
              <span className="text-slate-500 truncate">{r.address}</span>
              {r.reason && <span className="text-amber-700">— {r.reason}</span>}
            </div>
          ))}
          {pv.warnings.map((w) => <div key={w} className="text-xs text-rose-600 font-medium" data-testid="recipient-preview-warning">{w}</div>)}
        </div>
      )}
    </div>
  );
}
